/**
 * Cancellation Policy template — renderer + editor + default data.
 */

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export const CANCELLATION_DEFAULT = {
  hero: {
    eyebrow: "Customer Information · Store Policies",
    headline: "Cancellation Policy",
    hl_accent: "Policy",
    lead: "When you place an order with IMPORTREND, it enters our processing system promptly. To ensure smooth fulfilment, please review your order details carefully before confirming.",
    bubble1: "✓ Order confirmed",
    bubble2: "🔒 Processing begins",
  },
  notice: {
    headline: "No cancellation after order confirmation",
    body: "Once an order is confirmed, it cannot be cancelled or refunded for a cancellation request, except where a refund or other remedy is required by applicable consumer law.",
  },
  rules: {
    headline: "Our Cancellation Rules",
    body: "Simple, transparent information to help you shop with confidence.",
    cards: [
      { icon: "🔒", title: "Confirmed Orders Are Final", text: "After you confirm an order, processing begins immediately. Cancellation requests will not be accommodated, subject to applicable law." },
      { icon: "🚫", title: "No Cancellation Refund", text: "No refund is offered if you request cancellation of a confirmed order, except where required by applicable consumer protection law." },
      { icon: "⚡", title: "Order Processing", text: "Orders are processed promptly. Please check all items, delivery details, and payment information before confirming your purchase." },
    ],
  },
  checklist: {
    headline: "Before You Place an Order",
    body: "Take a moment to check these details before final confirmation.",
    steps: [
      { num: "01", title: "Review Your Items", desc: "Check that you have selected the correct products, sizes, quantities, and variants." },
      { num: "02", title: "Verify Delivery Details", desc: "Confirm your delivery address, including PIN code and contact number, is accurate." },
      { num: "03", title: "Confirm Your Payment", desc: "Review the total amount, any applied discounts, and your chosen payment method." },
    ],
  },
  damaged: {
    headline: "Damaged or Incorrect Product?",
    body: "If you receive a damaged or incorrect product, this is different from a cancellation and is covered under our separate Return & Refund Policy. Contact our support team within 24 hours of receiving your order with your order details and clear photos. Your statutory consumer rights remain unaffected by this cancellation policy.",
  },
  faq: {
    headline: "Frequently Asked Questions",
    body: "Answers to the most common questions about our cancellation policy.",
    items: [
      { question: "Can I cancel my order after confirmation?", answer: "No. Once your order is confirmed, cancellation is not available, except where required by applicable consumer law." },
      { question: "Will I get a refund if I ask to cancel?", answer: "No refund is offered for a cancellation request on a confirmed order, subject to applicable law." },
      { question: "What if I received a damaged or different product?", answer: "Please contact our support team within 24 hours of receiving the package. This is handled under our Return & Refund Policy, not the cancellation policy." },
      { question: "What if IMPORTREND cannot fulfil my order?", answer: "If we are unable to fulfil your order, we will contact you and provide an appropriate resolution or refund in accordance with applicable law." },
    ],
  },
  support: {
    headline: "Need Help With an Order?",
    body: "If you have any questions about your order, our support team is happy to help during business hours.",
    btn1_text: "✉ support@importrend.com",
    btn1_href: "mailto:support@importrend.com",
    btn2_text: "☎ +91 7011608563",
    btn2_href: "tel:+917011608563",
    hours: "Office hours: 09:00–18:00 · Monday to Saturday (IST)",
  },
};

const CP_CSS = `
.cp-root{--ink:#33251d;--orange:#e76c2e;--dark-orange:#bb4b18;--muted:#75675c;--cream:#fff8ef;--peach:#ffead8;--line:#f0dcc9;color:var(--ink);background:#fffaf5;font-family:system-ui,-apple-system,"Segoe UI",Arial,sans-serif;line-height:1.65}
.cp-root *{box-sizing:border-box}
.cp-root h1,.cp-root h2,.cp-root h3,.cp-root p{margin-top:0}
.cp-root a{color:var(--orange)}
.cp-wrap{width:min(1160px,calc(100% - 40px));margin:auto}
.cp-orange{color:var(--orange)}
.cp-eyebrow{font-size:.78rem;letter-spacing:.14em;text-transform:uppercase;font-weight:800;color:var(--orange);display:block;margin-bottom:10px}
.cp-hero{background:radial-gradient(circle at 85% 12%,#ffd9b8 0,transparent 38%),linear-gradient(125deg,#fffaf3,#fff2e3);padding:72px 0 60px;overflow:hidden}
.cp-hero-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:50px;align-items:center}
.cp-hero h1{font-size:clamp(2.5rem,4.8vw,5rem);line-height:1.05;letter-spacing:-.05em;margin-bottom:14px}
.cp-lead{font-size:1.05rem;color:var(--muted);max-width:560px}
.cp-hero-art{min-height:300px;border-radius:32px;background:linear-gradient(135deg,#ffd4a8,#ffe8cc);display:grid;place-items:center;position:relative;box-shadow:0 22px 60px #bf7b2c22}
.cp-box{font-size:clamp(80px,12vw,130px);filter:drop-shadow(0 12px 10px #b26a2828);z-index:1}
.cp-float{position:absolute;background:#fff;border-radius:16px;padding:10px 16px;font-size:.88rem;font-weight:800;box-shadow:0 10px 28px #8f4e2418}
.cp-float.one{top:22px;right:20px}.cp-float.two{bottom:22px;left:20px}
.cp-notice{background:#fff;border-left:4px solid var(--orange);border-radius:16px;padding:24px 28px;box-shadow:0 12px 38px #c07a2c16;margin:0 0 24px}
.cp-notice strong{display:block;font-size:1.05rem;margin-bottom:8px;color:var(--ink)}
.cp-notice p{color:var(--muted);margin:0}
.cp-section{padding:60px 0}
.cp-section.cp-soft{background:var(--cream)}
.cp-sh{text-align:center;max-width:680px;margin:0 auto 32px}
.cp-sh h2{font-size:clamp(1.6rem,2.8vw,2.6rem);letter-spacing:-.03em;margin-bottom:10px}
.cp-sh p{color:var(--muted)}
.cp-grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}
.cp-grid3-steps{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}
.cp-card{border-radius:20px;background:#fff;border:1px solid var(--line);padding:26px;box-shadow:0 8px 24px #6e3a0c08}
.cp-card p:last-child{margin-bottom:0}
.cp-icon{width:48px;height:48px;border-radius:14px;background:#ffe3cc;display:grid;place-items:center;font-size:1.5rem;margin-bottom:14px}
.cp-step-card{border-radius:20px;background:#fff;border:1px solid var(--line);padding:26px;box-shadow:0 8px 24px #6e3a0c08;position:relative}
.cp-step-card:before{counter-increment:steps;content:attr(data-num);position:absolute;top:20px;right:20px;font-size:1.6rem;font-weight:900;color:#f4dcc8}
.cp-highlight{display:flex;gap:24px;align-items:flex-start;background:linear-gradient(130deg,#fff4e8,#ffe8d4);border:1px solid #f0d9c4;border-radius:22px;padding:34px}
.cp-big-icon{font-size:3.5rem;flex-shrink:0}
.cp-faq{display:flex;flex-direction:column;gap:12px}
.cp-faq details{border:1px solid var(--line);border-radius:16px;background:#fff;padding:18px 22px;cursor:pointer}
.cp-faq summary{font-weight:700;font-size:.98rem;list-style:none}
.cp-faq summary::-webkit-details-marker{display:none}
.cp-faq p{margin:12px 0 0;color:var(--muted);font-size:.93rem}
.cp-contact{background:linear-gradient(130deg,var(--orange),var(--dark-orange));border-radius:24px;padding:42px 44px;color:#fff;text-align:center}
.cp-contact h2{color:#fff;font-size:clamp(1.5rem,2.5vw,2.2rem);margin-bottom:10px}
.cp-contact p{color:#ffe6d4;margin-bottom:0}
.cp-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin:20px 0 10px}
.cp-btn{display:inline-block;padding:12px 20px;border-radius:12px;background:#fff;color:#a44a18;text-decoration:none;font-weight:800}
.cp-btn-out{background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.5);color:#fff}
.cp-hours{font-size:.82rem;color:#ffddc6;margin-top:10px!important}
@media(max-width:800px){.cp-hero-grid{grid-template-columns:1fr}.cp-grid3,.cp-grid3-steps{grid-template-columns:1fr}.cp-hero-art{min-height:220px}.cp-highlight{flex-direction:column}}
@media(max-width:600px){.cp-wrap{width:calc(100% - 28px)}.cp-hero{padding:44px 0}.cp-section{padding:44px 0}.cp-contact{padding:30px 20px}}
`;

export function CancellationPolicyRenderer({ data }) {
  const d  = data || CANCELLATION_DEFAULT;
  const h  = d.hero      || CANCELLATION_DEFAULT.hero;
  const n  = d.notice    || CANCELLATION_DEFAULT.notice;
  const r  = d.rules     || CANCELLATION_DEFAULT.rules;
  const ch = d.checklist || CANCELLATION_DEFAULT.checklist;
  const dm = d.damaged   || CANCELLATION_DEFAULT.damaged;
  const fq = d.faq       || CANCELLATION_DEFAULT.faq;
  const s  = d.support   || CANCELLATION_DEFAULT.support;

  const accentify = (txt, acc) => {
    if (!acc || !txt.includes(acc)) return <span>{txt}</span>;
    const parts = txt.split(acc);
    return <span>{parts[0]}<span className="cp-orange">{acc}</span>{parts.slice(1).join(acc)}</span>;
  };

  return (
    <div className="cp-root">
      <style>{CP_CSS}</style>

      <section className="cp-hero">
        <div className="cp-wrap cp-hero-grid">
          <div>
            <span className="cp-eyebrow">{h.eyebrow}</span>
            <h1>{accentify(h.headline, h.hl_accent)}</h1>
            <p className="cp-lead">{h.lead}</p>
          </div>
          <div className="cp-hero-art">
            <div className="cp-float one">{h.bubble1}</div>
            <div className="cp-box">📦</div>
            <div className="cp-float two">{h.bubble2}</div>
          </div>
        </div>
      </section>

      <div className="cp-wrap" style={{paddingTop:"36px",paddingBottom:"0"}}>
        <div className="cp-notice">
          <strong>Important: {n.headline}</strong>
          <p>{n.body}</p>
        </div>
      </div>

      <section className="cp-section">
        <div className="cp-wrap">
          <div className="cp-sh">
            <h2>{r.headline}</h2>
            <p>{r.body}</p>
          </div>
          <div className="cp-grid3">
            {(r.cards||[]).map((c, i) => (
              <article key={i} className="cp-card">
                <div className="cp-icon">{c.icon}</div>
                <h3>{c.title}</h3>
                <p style={{color:"var(--muted)"}}>{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section cp-soft">
        <div className="cp-wrap">
          <div className="cp-sh">
            <h2>{ch.headline}</h2>
            <p>{ch.body}</p>
          </div>
          <div className="cp-grid3-steps" style={{counterReset:"steps"}}>
            {(ch.steps||[]).map((step, i) => (
              <div key={i} className="cp-step-card" data-num={step.num}>
                <h3 style={{marginBottom:"8px"}}>{step.title}</h3>
                <p style={{color:"var(--muted)",marginBottom:0,fontSize:".93rem"}}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section">
        <div className="cp-wrap">
          <div className="cp-highlight">
            <div className="cp-big-icon">🛡️</div>
            <div>
              <h2 style={{marginBottom:"10px"}}>{dm.headline}</h2>
              <p style={{color:"var(--muted)",marginBottom:0}}>{dm.body}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cp-section cp-soft">
        <div className="cp-wrap">
          <div className="cp-sh">
            <h2>{fq.headline}</h2>
            <p>{fq.body}</p>
          </div>
          <div className="cp-faq">
            {(fq.items||[]).map((item, i) => (
              <details key={i}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="cp-section">
        <div className="cp-wrap">
          <div className="cp-contact">
            <h2>{s.headline}</h2>
            <p>{s.body}</p>
            <div className="cp-btns">
              <a className="cp-btn" href={s.btn1_href}>{s.btn1_text}</a>
              <a className="cp-btn cp-btn-out" href={s.btn2_href}>{s.btn2_text}</a>
            </div>
            {s.hours && <p className="cp-hours">{s.hours}</p>}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, rows = 3 }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

function SectionBlock({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

export function CancellationPolicyEditor({ data, onChange }) {
  const d = data || CANCELLATION_DEFAULT;
  const set = (section, field, value) => onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  const setArrItem = (section, arrKey, idx, field, value) => {
    const arr = [...(d[section]?.[arrKey] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    onChange({ ...d, [section]: { ...(d[section] || {}), [arrKey]: arr } });
  };

  const h = d.hero || {};
  const n = d.notice || {};
  const r = d.rules || {};
  const ch = d.checklist || {};
  const dm = d.damaged || {};
  const fq = d.faq || {};
  const s = d.support || {};

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>Cancellation Policy Template</strong>
      </div>

      <SectionBlock title="1. Hero" defaultOpen>
        <Field label="Eyebrow" value={h.eyebrow} onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline" value={h.headline} onChange={v => set("hero","headline",v)} />
        <Field label="Accent word" value={h.hl_accent} onChange={v => set("hero","hl_accent",v)} />
        <Field label="Lead" value={h.lead} onChange={v => set("hero","lead",v)} multiline />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Bubble 1 text" value={h.bubble1} onChange={v => set("hero","bubble1",v)} />
          <Field label="Bubble 2 text" value={h.bubble2} onChange={v => set("hero","bubble2",v)} />
        </div>
      </SectionBlock>

      <SectionBlock title="2. Important Notice">
        <Field label="Headline" value={n.headline} onChange={v => set("notice","headline",v)} />
        <Field label="Body" value={n.body} onChange={v => set("notice","body",v)} multiline />
      </SectionBlock>

      <SectionBlock title="3. Cancellation Rules (3 cards)">
        <Field label="Headline" value={r.headline} onChange={v => set("rules","headline",v)} />
        <Field label="Body" value={r.body} onChange={v => set("rules","body",v)} multiline />
        {(r.cards||[]).map((c,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Icon" value={c.icon} onChange={v => setArrItem("rules","cards",i,"icon",v)} />
              <div className="col-span-3"><Field label="Title" value={c.title} onChange={v => setArrItem("rules","cards",i,"title",v)} /></div>
            </div>
            <Field label="Text" value={c.text} onChange={v => setArrItem("rules","cards",i,"text",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="4. Pre-Order Checklist">
        <Field label="Headline" value={ch.headline} onChange={v => set("checklist","headline",v)} />
        <Field label="Body" value={ch.body} onChange={v => set("checklist","body",v)} multiline />
        {(ch.steps||[]).map((step,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Field label="Num tag" value={step.num} onChange={v => setArrItem("checklist","steps",i,"num",v)} />
              <div className="col-span-3"><Field label="Title" value={step.title} onChange={v => setArrItem("checklist","steps",i,"title",v)} /></div>
            </div>
            <Field label="Description" value={step.desc} onChange={v => setArrItem("checklist","steps",i,"desc",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="5. Damaged Product Highlight">
        <Field label="Headline" value={dm.headline} onChange={v => set("damaged","headline",v)} />
        <Field label="Body" value={dm.body} onChange={v => set("damaged","body",v)} multiline rows={4} />
      </SectionBlock>

      <SectionBlock title="6. FAQ">
        <Field label="Headline" value={fq.headline} onChange={v => set("faq","headline",v)} />
        <Field label="Body" value={fq.body} onChange={v => set("faq","body",v)} multiline />
        {(fq.items||[]).map((item,i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2">
            <Field label="Question" value={item.question} onChange={v => setArrItem("faq","items",i,"question",v)} />
            <Field label="Answer" value={item.answer} onChange={v => setArrItem("faq","items",i,"answer",v)} multiline />
          </div>
        ))}
      </SectionBlock>

      <SectionBlock title="7. Support CTA">
        <Field label="Headline" value={s.headline} onChange={v => set("support","headline",v)} />
        <Field label="Body" value={s.body} onChange={v => set("support","body",v)} multiline />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 1 text" value={s.btn1_text} onChange={v => set("support","btn1_text",v)} />
          <Field label="Button 1 href" value={s.btn1_href} onChange={v => set("support","btn1_href",v)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Button 2 text" value={s.btn2_text} onChange={v => set("support","btn2_text",v)} />
          <Field label="Button 2 href" value={s.btn2_href} onChange={v => set("support","btn2_href",v)} />
        </div>
        <Field label="Hours text" value={s.hours} onChange={v => set("support","hours",v)} />
      </SectionBlock>
    </div>
  );
}
